import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Tabs, Tab, Paper, CircularProgress, Alert } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import SEO from '../components/SEO';

type TabValue = 'customer' | 'provider' | 'privacy';

type LegalDoc = {
  endpoint: string;
  field: 'agreement' | 'policy';
  title: string;
  path: string;
};

const DOCS: Record<TabValue, LegalDoc> = {
  customer: {
    endpoint: '/legal/customer-agreement',
    field: 'agreement',
    title: 'Customer Service Agreement',
    path: '/legal?tab=customer',
  },
  provider: {
    endpoint: '/legal/provider-agreement',
    field: 'agreement',
    title: 'Provider Service Agreement',
    path: '/legal?tab=provider',
  },
  privacy: {
    endpoint: '/legal/privacy-policy',
    field: 'policy',
    title: 'Privacy Policy',
    path: '/privacy-policy',
  },
};

const TAB_ORDER: TabValue[] = ['customer', 'provider', 'privacy'];

const isTabValue = (v: string | null): v is TabValue => !!v && (TAB_ORDER as string[]).includes(v);

const LegalPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: TabValue = location.pathname === '/privacy-policy'
    ? 'privacy'
    : isTabValue(tabParam) ? tabParam : 'customer';

  const [texts, setTexts] = useState<Partial<Record<TabValue, string>>>({});
  const [failed, setFailed] = useState<Partial<Record<TabValue, boolean>>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const results = await Promise.allSettled(
        TAB_ORDER.map((key) => apiService.get(DOCS[key].endpoint) as Promise<{ success?: boolean; data?: Record<string, string> }>)
      );
      if (cancelled) return;
      const nextTexts: Partial<Record<TabValue, string>> = {};
      const nextFailed: Partial<Record<TabValue, boolean>> = {};
      results.forEach((result, i) => {
        const key = TAB_ORDER[i];
        const text = result.status === 'fulfilled' && result.value?.success
          ? result.value.data?.[DOCS[key].field]
          : undefined;
        if (text) nextTexts[key] = text;
        else nextFailed[key] = true;
      });
      setTexts(nextTexts);
      setFailed(nextFailed);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const doc = DOCS[tab];
  const content = texts[tab];

  return (
    <Box sx={{ minHeight: '80vh', py: 4 }}>
      {tab === 'privacy' ? (
        <SEO
          title="Privacy Policy | LawnHub"
          description="How LawnHub collects, uses, and shares personal information for website visitors, homeowners, and lawn care and snow removal providers."
          url="/privacy-policy"
        />
      ) : (
        <SEO
          title="Legal | LawnHub"
          description="LawnHub Customer Service Agreement, Provider Service Agreement, and Privacy Policy."
          url="/legal"
        />
      )}
      <Container maxWidth="md">
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          Legal
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Full text of our service agreements and Privacy Policy.
        </Typography>

        <Tabs
          value={tab}
          onChange={(_, v: TabValue) => navigate(DOCS[v].path, { replace: true })}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ mb: 2 }}
        >
          {TAB_ORDER.map((key) => (
            <Tab key={key} label={DOCS[key].title} value={key} />
          ))}
        </Tabs>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && failed[tab] && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not load the {doc.title}. Please try again later or email legal@illinoislawnhub.com.
          </Alert>
        )}

        {!loading && content && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              {doc.title}
            </Typography>
            <Box
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'body2.fontFamily',
                fontSize: '0.875rem',
                lineHeight: 1.6,
                maxHeight: '70vh',
                overflow: 'auto',
                m: 0,
              }}
            >
              {content}
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default LegalPage;
